import React, { useMemo } from "react"
import {
	Box, Chip, IconButton, MenuItem, Select, Stack, TextField,
	Typography,
} from "@mui/material"
import { DeleteOutline as DeleteIcon, Add as AddIcon } from 'Pages/DsRepricing/@components/icons'
import { AppNote } from "@components"
import { dsColorNew } from "@palette"
import { rangeWellSx } from "Pages/DsRepricing/Page/@components/rangeSx"
import { userData } from "@signals"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import {
	KPI_BY_CODE, KPI_OPERATORS, describeCondition, availableKpis,
} from "Pages/DsRepricing/Page/@components/kpiCatalog"

/**
 * "IF · My Business" gate — activation conditions that the engine
 * evaluates BEFORE running the strategy for a given rate shop. If any
 * condition fails, the strategy is skipped this run (audit event
 * still emitted so the operator can see it was gated).
 *
 * Model: `advanced: { kpi_conditions: [{kpi, op, value}, ...] }`.
 * Empty list == no gate == strategy always runs.
 * All conditions AND-ed together.
 */
export default function DsRepricingKpiConditions() {
	// Only the KPIs this operator may use: the forecast one needs its
	// feature key, and offering a KPI the server will refuse would be
	// offering a strategy that cannot be saved (brief criterio 20).
	const catalog = useMemo(
		() => availableKpis(userData.value?.featuresV2),
		[userData.value?.featuresV2])
	const categories = useMemo(
		() => [...new Set(catalog.map(k => k.category))], [catalog])

	const data = dsRepricingDrawerData.value
	const conditions = data.advanced?.kpi_conditions ?? []

	function commit(next) {
		dsRepricingDrawerData.value = {
			...dsRepricingDrawerData.peek(),
			advanced: next.length === 0 ? null : { kpi_conditions: next },
		}
	}
	function updateAt(idx, patch) {
		commit(conditions.map((c, i) => i === idx ? { ...c, ...patch } : c))
	}
	function removeAt(idx) {
		commit(conditions.filter((_, i) => i !== idx))
	}
	function add() {
		const firstUnused = catalog.find(k => !conditions.some(c => c.kpi === k.code)) || catalog[0]
		commit([...conditions, { kpi: firstUnused.code, op: '<', value: null }])
	}

	return (
		<Box>
			<AppNote sx={{ mb: 2 }}>
				<Box sx={{ fontWeight: 600, mb: 0.25 }}>
					Only apply this strategy when ALL of these hold true
				</Box>
				<Box sx={{ fontSize: 12 }}>
					The engine reads these KPIs from your PMS at the moment of the
					rate shop. If any condition fails, the strategy is skipped and a{' '}
					<em>quarantined</em> event is emitted so you can see it was gated.
					Empty list = no gate.
				</Box>
			</AppNote>

			<Box sx={{
				...rangeWellSx,
				gap: 1,
			}}>
				{conditions.length === 0 ? (
					<Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle,
					                  fontStyle: 'italic', textAlign: 'center', py: 1.5 }}>
						No conditions yet — the strategy will run on every eligible rate
						shop. Add one to gate it on a PMS metric.
					</Typography>
				) : (
					<Stack gap={1}>
						{conditions.map((c, i) => (
							<ConditionRow key={i}
							              condition={c}
							              catalog={catalog}
							              categories={categories}
							              onChange={(patch) => updateAt(i, patch)}
							              onRemove={() => removeAt(i)}/>
						))}
					</Stack>
				)}

				<Box sx={{ display: 'flex', justifyContent: 'flex-start',
				           pt: conditions.length > 0 ? 1 : 0,
				           borderTop: conditions.length > 0 ? `1px dashed ${dsColorNew.grayBorder}` : 'none' }}>
					<Box component="button" type="button"
					     onClick={add}
					     sx={{
					         appearance: 'none', cursor: 'pointer',
					         display: 'inline-flex', alignItems: 'center', gap: 0.75,
					         px: 1.5, py: 0.75, minHeight: 36,
					         border: '1px dashed', borderColor: dsColorNew.grayBorder,
					         bgcolor: dsColorNew.white,
					         color: dsColorNew.textMuted,
					         fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
					         borderRadius: '8px',
					         '&:hover': {
					             borderStyle: 'solid',
					             borderColor: dsColorNew.appBlue,
					             color: dsColorNew.appBlue,
					             bgcolor: dsColorNew.pillActiveBg,
					         },
					     }}>
						<AddIcon sx={{ fontSize: 18 }}/>
						Add condition
					</Box>
				</Box>
			</Box>

			{conditions.length > 0 && (
				<Box sx={{ mt: 1.5, p: 1.25,
				           bgcolor: dsColorNew.tableRowAlt, border: `1px solid ${dsColorNew.grayBorder}`,
				           borderRadius: '10px' }}>
					<Typography sx={{ fontSize: 11, fontWeight: 600,
					                  textTransform: 'uppercase', letterSpacing: 0.6,
					                  color: dsColorNew.textSubtle, mb: 0.5 }}>
						Preview
					</Typography>
					<Typography sx={{ fontSize: 13, lineHeight: 1.6 }}>
						{conditions.map((c, i) => (
							<React.Fragment key={i}>
								{i > 0 && (
									<Box component="span" sx={{ mx: 0.75, color: dsColorNew.appBlue,
									                            fontWeight: 700, fontSize: 12,
									                            textTransform: 'uppercase',
									                            letterSpacing: '0.06em' }}>
										AND
									</Box>
								)}
								<Box component="span" sx={{ fontFamily: 'inherit',
								                            fontWeight: 700 }}>
									{describeCondition(c)}
								</Box>
							</React.Fragment>
						))}
					</Typography>
				</Box>
			)}
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Condition row: [KPI select] [op select] [value input]  ×
// ---------------------------------------------------------------------------
function ConditionRow({ condition, catalog, categories, onChange, onRemove }) {
	const kpi = KPI_BY_CODE[condition.kpi]
	return (
		<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
			<Box sx={{ flex: '1 1 260px' }}>
				<Select fullWidth size="small"
				        value={condition.kpi}
				        onChange={(e) => onChange({ kpi: e.target.value })}
				        sx={{ bgcolor: dsColorNew.white,
				              '& .MuiOutlinedInput-root': { height: 40 } }}>
					{categories.map(cat => [
						<CategoryHeader key={`h-${cat}`}>{cat}</CategoryHeader>,
						...catalog.filter(k => k.category === cat).map(k => (
							<MenuItem key={k.code} value={k.code} sx={{ pl: 3 }}>
								<Stack direction="row" gap={0.75} alignItems="center" sx={{ width: '100%' }}>
									<Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
										{k.label}
									</Typography>
									<Chip label={k.unit} size="small"
									      sx={{ height: 18, fontSize: 11, fontWeight: 700,
									            bgcolor: dsColorNew.pillTrackBg, color: dsColorNew.textMuted }}/>
								</Stack>
							</MenuItem>
						)),
					])}
				</Select>
			</Box>

			<Select size="small"
			        value={condition.op}
			        onChange={(e) => onChange({ op: e.target.value })}
			        sx={{ flex: '0 0 auto', minWidth: 68,
			              bgcolor: dsColorNew.white,
			              '& .MuiOutlinedInput-root': { height: 40 },
			              '& .MuiSelect-select': { textAlign: 'center',
			                                        fontWeight: 700, fontSize: 14,
			                                        py: '9px' } }}>
				{KPI_OPERATORS.map(o => (
					<MenuItem key={o.code} value={o.code}>
						<Box component="span" sx={{ fontWeight: 700, fontSize: 14,
						                            width: 20, textAlign: 'center' }}>
							{o.label}
						</Box>
						<Typography component="span" sx={{ ml: 1, fontSize: 12,
						                                    color: dsColorNew.textMuted }}>
							{o.description}
						</Typography>
					</MenuItem>
				))}
			</Select>

			<Box sx={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<TextField type="number" size="small"
				           value={condition.value ?? ''}
				           onChange={(e) => onChange({
				               value: e.target.value === '' ? null : Number(e.target.value),
				           })}
				           inputProps={{
				               min: kpi?.min, max: kpi?.max, step: kpi?.step,
				               style: { width: 90, textAlign: 'right',
				                        fontWeight: 700, fontSize: 13 },
				           }}
				           sx={{ bgcolor: dsColorNew.white,
				                 '& .MuiOutlinedInput-root': { height: 40 } }}/>
				{kpi?.unit && (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
					                  fontWeight: 600, minWidth: 40 }}>
						{kpi.unit}
					</Typography>
				)}
			</Box>

			<IconButton size="small" onClick={onRemove}
			            title="Remove condition"
			            sx={{ color: dsColorNew.textMuted, mt: 0.5,
			                  '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }}>
				<DeleteIcon fontSize="small"/>
			</IconButton>
		</Box>
	)
}

function CategoryHeader({ children }) {
	return (
		<MenuItem disabled sx={{
			fontSize: 11, letterSpacing: '0.06em',
			textTransform: 'uppercase', fontWeight: 700,
			color: dsColorNew.textMuted, opacity: '1 !important',
			bgcolor: dsColorNew.tableRowAlt, py: 0.5,
		}}>
			{children}
		</MenuItem>
	)
}
